import { Box, ButtonBase, Collapse, Stack, SvgIcon } from '@mui/material'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useState } from 'react'
import ChevronDownIcon from '@/icons/ChevronDown'
import ChevronRightIcon from '@/icons/ChevronRight'
import { NavItem } from './NavItem'

const renderItems = ({ depth = 0, items, pathname }) =>
    items.reduce(
        (acc, item) =>
            // eslint-disable-next-line no-use-before-define
            reduceChildRoutes({ acc, depth, item, pathname }),
        []
    )

const reduceChildRoutes = ({ acc, depth, item, pathname }) => {
    const checkPath = !!(item.path && pathname)
    const partialMatch = checkPath ? pathname.includes(item.path) : false
    const exactMatch = checkPath ? pathname === item.path : false

    if (item.visible) {
        if (item.items) {
            acc.push(
                <NavItem
                    active={partialMatch}
                    depth={depth}
                    disabled={item.disabled}
                    icon={item.icon}
                    key={item.title}
                    label={item.label}
                    open={partialMatch}
                    title={item.title}
                >
                    <Stack
                        component="ul"
                        spacing={0.5}
                        sx={{
                            listStyle: 'none',
                            m: 0,
                            p: 0
                        }}
                    >
                        {renderItems({
                            depth: depth + 1,
                            items: item.items,
                            pathname
                        })}
                    </Stack>
                </NavItem>
            )
        } else {
            acc.push(
                <NavItem
                    active={exactMatch}
                    depth={depth}
                    disabled={item.disabled}
                    external={item.external}
                    icon={item.icon}
                    key={item.title}
                    label={item.label}
                    path={item.path}
                    title={item.title}
                />
            )
        }
    }

    return acc
}

// Number of leaf docs in a section (recurses into nested groups, though the
// docs sidebar is currently flat).
const countDocs = (items = []) =>
    items.reduce(
        (total, item) => total + (item.items ? countDocs(item.items) : 1),
        0
    )

// Does any doc in this section match the current URL? Used to auto-expand the
// section the visitor just landed on.
const sectionContainsPath = (items = [], pathname) =>
    items.some((item) =>
        item.items
            ? sectionContainsPath(item.items, pathname)
            : item.path === pathname
    )

export const NavSection = (props) => {
    // `visible`/`roles` are data-model flags from the sidebar config; pull them
    // out of `...other` so they aren't spread onto the DOM node.
    const {
        items = [],
        pathname,
        subheader = '',
        visible = true,
        roles,
        ...other
    } = props

    const containsActive = sectionContainsPath(items, pathname)
    const [open, setOpen] = useState(containsActive)

    // Auto-expand when navigating into this section. This only ever opens a
    // section (never force-closes), so sections the user opened by hand stay
    // open as they browse.
    useEffect(() => {
        if (containsActive) {
            setOpen(true)
        }
    }, [containsActive])

    const handleToggle = useCallback(() => {
        setOpen((prevOpen) => !prevOpen)
    }, [])

    if (!visible) {
        return null
    }

    const docCount = countDocs(items)

    return (
        <Box {...other}>
            <ButtonBase
                aria-expanded={open}
                onClick={handleToggle}
                sx={{
                    alignItems: 'center',
                    borderRadius: 1,
                    display: 'flex',
                    px: 1,
                    py: 0.5,
                    width: '100%',
                    '&:hover': {
                        backgroundColor: 'var(--nav-item-hover-bg)'
                    }
                }}
            >
                <Box
                    component="span"
                    sx={{
                        color: 'var(--nav-section-title-color)',
                        flexGrow: 1,
                        fontSize: 12,
                        fontWeight: 700,
                        lineHeight: 1.66,
                        overflow: 'hidden',
                        textAlign: 'left',
                        textOverflow: 'ellipsis',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {subheader}
                </Box>
                <Box
                    component="span"
                    sx={{
                        backgroundColor: 'var(--nav-item-active-bg)',
                        borderRadius: 1,
                        color: 'var(--nav-section-title-color)',
                        fontSize: 11,
                        fontWeight: 600,
                        lineHeight: 1.5,
                        ml: 1,
                        minWidth: 18,
                        px: 0.5,
                        textAlign: 'center'
                    }}
                >
                    {docCount}
                </Box>
                <SvgIcon
                    sx={{
                        color: 'var(--nav-item-chevron-color)',
                        fontSize: 16,
                        ml: 0.5
                    }}
                >
                    {open ? <ChevronDownIcon /> : <ChevronRightIcon />}
                </SvgIcon>
            </ButtonBase>
            <Collapse in={open}>
                <Stack
                    component="ul"
                    spacing={0.5}
                    sx={{
                        listStyle: 'none',
                        m: 0,
                        mt: 0.5,
                        p: 0
                    }}
                >
                    {renderItems({ items, pathname })}
                </Stack>
            </Collapse>
        </Box>
    )
}

NavSection.propTypes = {
    items: PropTypes.array,
    pathname: PropTypes.string,
    roles: PropTypes.array,
    subheader: PropTypes.string,
    visible: PropTypes.bool
}
